<template>
    <div class="circular-graph" data-cy="container_circular_graph" :style="containerStyle">
        <!-- SVG for rings -->
        <svg :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
            <!-- Each ring: background + foreground together -->
            <g v-for="(ring, index) in computedRings" :key="index">
                <!-- Background ring (gray track) - clickable -->
                <circle
                    :cx="center"
                    :cy="center"
                    :r="ring.radius"
                    fill="none"
                    :stroke="ring.backgroundColor"
                    :stroke-width="strokeWidth"
                    class="ring-clickable"
                    @click="handleRingClick(index)"
                />
                <!-- Foreground ring (colored progress) - clickable -->
                <circle
                    :cx="center"
                    :cy="center"
                    :r="ring.radius"
                    fill="none"
                    :stroke="ring.color"
                    :stroke-width="strokeWidth"
                    :stroke-dasharray="ring.dashArray"
                    :stroke-dashoffset="ring.dashOffset"
                    :stroke-linecap="strokeLinecap"
                    :transform="`rotate(-90 ${center} ${center})`"
                    class="ring-progress ring-clickable"
                    :style="getProgressStyle(index)"
                    @click="handleRingClick(index)"
                />
            </g>
        </svg>
        
        <!-- Center text -->
        <div class="center-text" data-cy="text_circular_graph_score">
            <span class="score-value" :style="scoreValueStyle" data-cy="text_circular_graph_score_value">{{ displayScore }}</span>
            <span class="score-max" :style="scoreMaxStyle" data-cy="text_circular_graph_score_max">/{{ maxScore }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: "CircularGraph",
    emits: ['ring-click'],
    props: {
        // Array of ring configurations
        // Each ring: { value: number (0-100), color: string, backgroundColor: string }
        rings: {
            type: Array,
            required: true,
            default: () => []
        },
        // Center score to display
        score: {
            type: Number,
            default: 0
        },
        // Maximum score
        maxScore: {
            type: Number,
            default: 100
        },
        // Overall size in pixels
        size: {
            type: Number,
            default: 200
        },
        // Stroke width for each ring
        strokeWidth: {
            type: Number,
            default: 12
        },
        // Gap between rings
        gap: {
            type: Number,
            default: 8
        },
        // Stroke line cap style: 'round' or 'butt'
        strokeLinecap: {
            type: String,
            default: 'butt'
        },
        // Enable animation
        animated: {
            type: Boolean,
            default: true
        },
        // Animation duration in milliseconds
        animationDuration: {
            type: Number,
            default: 800
        },
        // Score text color
        scoreColor: {
            type: String,
            default: '#1a1a1a'
        },
        // Score font size (auto-calculated if not provided)
        scoreFontSize: {
            type: Number,
            default: 0
        }
    },
    computed: {
        center() {
            return this.size / 2;
        },
        displayScore() {
            return Math.round(this.score);
        },
        containerStyle() {
            return {
                width: this.size + 'px',
                height: this.size + 'px'
            };
        },
        computedRings() {
            const rings = [];
            const totalRings = this.rings.length;
            
            // Calculate starting radius (outermost ring)
            const maxRadius = (this.size / 2) - (this.strokeWidth / 2) - 4;
            
            for (let i = 0; i < totalRings; i++) {
                const ring = this.rings[i];
                // Outer ring first, then inner rings
                const radius = maxRadius - (i * (this.strokeWidth + this.gap));
                const circumference = 2 * Math.PI * radius;
                const value = Math.min(Math.max(ring.value || 0, 0), 100);
                // Calculate dashoffset for progress (start from top, go clockwise)
                const dashOffset = circumference * (1 - value / 100);
                
                rings.push({
                    radius: radius,
                    circumference: circumference,
                    dashArray: circumference,
                    dashOffset: dashOffset,
                    color: ring.color || '#2140a6',
                    backgroundColor: ring.backgroundColor || '#e0e0e0'
                });
            }
            
            return rings;
        },
        // Calculate font size based on inner space available
        calculatedFontSize() {
            if (this.scoreFontSize > 0) {
                return this.scoreFontSize;
            }
            // Calculate based on innermost ring
            const totalRings = this.rings.length;
            const maxRadius = (this.size / 2) - (this.strokeWidth / 2) - 4;
            const innerRadius = maxRadius - ((totalRings - 1) * (this.strokeWidth + this.gap)) - this.strokeWidth;
            // Font size should be about 55% of the inner diameter (smaller for better fit)
            return Math.floor(innerRadius * 0.55);
        },
        scoreValueStyle() {
            return {
                fontSize: 32+ 'px', // this.calculatedFontSize + 'px',
                color: this.scoreColor
            };
        },
        scoreMaxStyle() {
            return {
                fontSize: 14 + 'px', // Math.floor(this.calculatedFontSize * 0.45) + 'px',
                color: this.scoreColor,
                fontWeight: 'bold'
            };
        }
    },
    methods: {
        getProgressStyle(index) {
            if (!this.animated) {
                return {};
            }
            return {
                transition: `stroke-dashoffset ${this.animationDuration}ms ease-out`,
                transitionDelay: `${index * 100}ms`
            };
        },
        /**
         * Calculate the endpoint position of a ring arc
         * @param {number} index - Ring index
         * @returns {Object} - { x, y, angle } endpoint position relative to container
         */
        getRingEndpoint(index) {
            const ring = this.computedRings[index];
            if (!ring) return { x: 0, y: 0, angle: 0 };
            
            const value = this.rings[index]?.value || 0;
            // Arc starts at top (12 o'clock, -90 degrees) and goes clockwise
            // End angle in radians: (value/100) * 2 * PI - PI/2
            const endAngle = (value / 100) * 2 * Math.PI - Math.PI / 2;
            
            // Calculate endpoint coordinates
            const x = this.center + ring.radius * Math.cos(endAngle);
            const y = this.center + ring.radius * Math.sin(endAngle);
            
            return {
                x,
                y,
                angle: (value / 100) * 360, // Degrees from top
                radius: ring.radius
            };
        },
        /**
         * Handle ring click event
         * @param {number} index - Ring index that was clicked
         */
        handleRingClick(index) {
            const endpoint = this.getRingEndpoint(index);
            this.$emit('ring-click', {
                index,
                endpoint,
                ring: this.rings[index],
                center: this.center,
                size: this.size
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.circular-graph {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
        position: absolute;
        top: 0;
        left: 0;
    }
    
    .ring-clickable {
        cursor: pointer;
        
        &:hover {
            opacity: 0.85;
        }
    }
    
    .center-text {
        position: relative;
        display: flex;
        align-items: baseline;
        justify-content: center;
        z-index: 1;
        
        .score-value {
            font-weight: bold;
            line-height: 1;
        }
        
        .score-max {
            font-weight: normal;
            line-height: 1;
            opacity: 0.6;
        }
    }
}
</style>
